import NextLink from 'next/link';
import {useRouter} from 'next/router';
import type {StaticImageData} from 'next/image';
import {useIntl} from 'react-intl';
import {Text, LinkBox, LinkOverlay} from '@chakra-ui/layout';

import {useTranslate} from '@eksab/i18n';
import {Image} from '@eksab/components/Image';
import {BackIcon} from '@eksab/assets/icons/BackIcon';
import {getLanguage} from '@eksab/util/helpers';

import type {Game} from '../types';

interface PlayCardProps {
  name: Game | 'groups';
  image: StaticImageData;
  route: string;
  count: number | null | undefined;
}

export function PlayCard({name, image, route, count}: PlayCardProps) {
  const {locale} = useRouter();
  const format = useIntl().formatNumber;

  const t = useTranslate();

  const dir = getLanguage(locale) === 'ar' ? 1 : -1;

  return (
    <LinkBox
      as="section"
      title={`${t('play')} ${t(`play.game.${name}.title`)}`}
      display="grid"
      data-group="play-card"
      data-game={name}
      gridTemplateColumns="auto 1fr auto"
      alignItems="center"
      columnGap={[3, 4]}
      rowGap={[1.5, 2, 3]}
      p={[4, 5, 6]}
      bg="#f7fdf9"
      rounded="3xl"
      borderWidth="1px"
      borderColor="green.400"
      cursor="pointer"
      overflow="hidden"
    >
      <Image
        src={image}
        alt={name}
        width="100"
        height="100"
        w={[10, 12, 14]}
        h="auto"
        maxH="4.5rem"
        objectFit="contain"
        gridRow={['1 / span 3', null, '1 / span 2']}
        fontSize="xx-small"
        rounded="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      />

      <Text as="h2" gridRow="1" gridColumn="2 / span 2" fontSize={['lg', 'xl', '2xl']} fontWeight="800">
        {t(`play.game.${name}.title`)}
      </Text>

      {count !== null && (
        <Text
          gridRow={[2, null, 1]}
          gridColumn="2 / span 2"
          justifySelf={['start', null, 'end']}
          px={[2, 3]}
          py={[1, 1.5]}
          bgColor="#ccedd6"
          fontSize={['xs', 'sm', 'md']}
          fontWeight="500"
          rounded="full"
          transform="auto"
          translateX={count === undefined ? `${-dir * 200}%` : 0}
          opacity={count === undefined ? 0 : 1}
          transition="transform 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55), opacity 0.3s"
        >
          {t('play.competitions.count', {
            count: (
              <Text as="strong" fontWeight="800">
                {format(count ?? 0)}
              </Text>
            ),
          })}
        </Text>
      )}

      <Text alignSelf="start" color="#6c6c6c" fontSize={['xs', 'sm', 'md']} fontWeight="600">
        {t(`play.game.${name}.description`)}
      </Text>

      <LinkOverlay as={NextLink} href={`/play/${route}`} me="4">
        <BackIcon
          transform="auto"
          scaleX={dir}
          transition="transform 0.3s, color 0.3s"
          _groupHover={{color: 'green.400', scaleX: dir * 1.25, scaleY: 1.25, translateX: `calc(${-dir * 0.5}rem)`}}
        />
      </LinkOverlay>
    </LinkBox>
  );
}
